'use client';

import { Icon } from '@iconify/react';
import { Button, Progress } from '@heroui/react';
import { useQuery } from '@tanstack/react-query';

import type { VideoProgressBlock, VideoThemesBlock } from '@/lib/reel-studio/agent/blocks';
import type { SynthesisResponse } from '@/lib/reel-studio/types';

import { OpenInReelStudio } from './OpenInReelStudio';
import { VideoThemesBlockView } from './VideoThemesBlockView';

/** How often the block asks whether the run has finished. */
const POLL_MS = 5_000;

function scopeParams(block: VideoProgressBlock): URLSearchParams {
    return new URLSearchParams({
        surveyIds: block.scope.surveyIds.join(','),
        questionId: block.scope.questionId,
    });
}

/**
 * An analysis run, live in the chat: polls the synthesis until the run
 * completes or fails, then swaps in the themes. The run carries on on the
 * server if the tab closes; reopening the chat polls it again.
 */
export function VideoProgressBlockView({ block }: { block: VideoProgressBlock }) {
    const synthesis = useQuery({
        queryKey: ['dani-reels-synthesis', block.scope.surveyIds, block.scope.questionId],
        enabled: block.status === 'processing',
        queryFn: async (): Promise<SynthesisResponse> => {
            const res = await fetch(`/api/dani/reel-studio/synthesis?${scopeParams(block)}`);
            if (!res.ok) throw new Error(`Could not check the analysis (${res.status})`);
            return (await res.json()) as SynthesisResponse;
        },
        refetchInterval: (query) =>
            query.state.data?.status === 'processing' || !query.state.data ? POLL_MS : false,
    });

    const finished = synthesis.data?.status === 'complete';
    const themes = useQuery({
        queryKey: ['dani-reels-themes', block.scope.surveyIds, block.scope.questionId],
        enabled: finished,
        queryFn: async (): Promise<VideoThemesBlock | null> => {
            const params = scopeParams(block);
            params.set('projectId', block.scope.projectId);
            const res = await fetch(`/api/dani/reel-studio/chat/themes?${params}`);
            if (!res.ok) throw new Error(`Could not load the themes (${res.status})`);
            const json = (await res.json()) as { status: string; block?: VideoThemesBlock };
            return json.status === 'complete' && json.block ? json.block : null;
        },
    });

    if (themes.data) return <VideoThemesBlockView block={themes.data} />;

    const failed = synthesis.data?.status === 'failed' || synthesis.isError || themes.isError;
    const empty = block.status === 'empty' || synthesis.data?.status === 'empty';

    let title: string;
    let detail: string;
    if (empty) {
        title = 'No transcripts to analyse yet';
        detail = 'None of these videos has a transcript yet.';
    } else if (failed) {
        title = 'The analysis did not finish';
        detail =
            synthesis.data?.error && synthesis.data.status === 'failed'
                ? 'Something went wrong while reading the videos. Ask DANI to run it again.'
                : 'Could not check on the analysis. Try again in a moment.';
    } else {
        title = finished ? 'Analysis complete' : 'Analysing the videos';
        const transcribed =
            block.transcribed != null ? `${block.transcribed.toLocaleString()} transcribed` : null;
        const collected =
            block.collected != null ? `${block.collected.toLocaleString()} collected` : null;
        detail = [transcribed, collected].filter(Boolean).join(' of ') || 'Reading the transcripts';
        if (!finished) detail += ' · usually a few minutes';
    }

    return (
        <section className="my-3 flex flex-col gap-3 rounded-xl border border-default-200 bg-content2/40 p-4">
            <header className="flex flex-wrap items-baseline justify-between gap-2">
                <div>
                    <h4 className="flex items-center gap-2 text-sm font-semibold text-foreground">
                        <Icon
                            icon={
                                failed
                                    ? 'lucide:alert-triangle'
                                    : empty
                                      ? 'lucide:captions-off'
                                      : 'lucide:sparkles'
                            }
                            width={16}
                        />
                        {title}
                    </h4>
                    <p
                        className="line-clamp-2 text-xs text-default-500"
                        title={block.scope.questionText}
                    >
                        {block.scope.projectName} · {block.scope.questionText}
                    </p>
                </div>
                <OpenInReelStudio scope={block.scope} tab="synthesis" />
            </header>
            {!failed && !empty && (
                <Progress
                    size="sm"
                    isIndeterminate
                    aria-label="Analysis in progress"
                    className="max-w-md"
                />
            )}
            <p className="text-xs text-default-500">{detail}</p>
            {failed && !empty && (
                <Button
                    size="sm"
                    variant="flat"
                    className="self-start"
                    onPress={() => {
                        void synthesis.refetch();
                        void themes.refetch();
                    }}
                >
                    Check again
                </Button>
            )}
        </section>
    );
}
